import { useMemo, useState } from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { Box, Button, Grid2, Stack, Typography, useTheme } from "@mui/material";
import { EGAccordionSingle } from "../../../../MainComponents/Accordion/EGAccordion/EGAccordion";
import { InvestigationItemContainer } from "../components/InvestigationItemContainer";
import dayjs from "dayjs";
import { InvestigationBlockInfo } from "../components/InvestigationBlockInfo";
import {
  ChipParticipant,
  EvaluationPoint,
  Separator,
  TemporalUploadedFile,
  UploadedFile,
  InvestigationCollapsable,
} from "../components/InvestigationComponents";
import { InvestigationButton } from "../components/InvestigationButton";
import { AddAttachments } from "./Modals/AddAttachments";
import { EvaluateInterview } from "./Modals/EvaluateInterview";
import { AddConclusion } from "./Modals/AddConclusion";
import { RichTextEditor } from "../../../../MainComponents/RichTextEditor/RichTextEditor";
import Icon from "../../../../Icon/Icon";
import { AddQuestionsToInterview } from "./Modals/AddQuestionsToInterview";
import { useConstants } from "../Main/useConstants";
import { useSelector } from "react-redux";

export const SingleInterview = ({
  idx,
  interview,
  interviews,
  setInterviews,
  setSelectedInterview,
  handleSaveInterviews,
  setIsAdding,
  justView,
  openConfirmDeleteModal,
  closeModalConfirm,
  alias,
  onReload,
}) => {
  const theme = useTheme();
  const [t] = useTranslation("investigation");
  const jaguarState = useSelector((state) => state.jaguar);
  const {
    enums,
    interviews: {
      typeInterviewOpts,
      statusOpts,
      rolesOpts,
      typeFileOpts,
      pointsToEvaluateLabels,
      options,
    },
    functions: {
      getUserLabel,
    },
  } = useConstants();

  const [showAddAttachments, setShowAddAttachments] = useState(false);
  const [showEvaluateInterview, setShowEvaluateInterview] = useState(false);
  const [showAddConclusion, setShowAddConclusion] = useState(false);
  const [showAddQuestionsToInterview, setShowAddQuestionsToInterview] =
    useState(false);
  const [selectedQuestionForEdit, setSelectedQuestionForEdit] = useState(null);

  const [selectTypeFileOpts, setSelectTypeFileOpts] = useState([]);

  const interviewed = useMemo(() => {
    if (typeof interview.interviewed === "string") {
      return interview.interviewed;
    }
    if (interview.interviewed?.from === enums.typeUser.COMPLAINANT) {
      return t("del Denunciante");
    }
    if (interview.interviewed?.from === enums.typeUser.LEADER) {
      return "del Lider";
    }
    if (interview.interviewed?.from === enums.typeUser.PEOPLE) {
      return "de People";
    }
    if (interview.interviewed?.from === "other") {
      return interview.interviewed?.id ? `de ${interview.interviewed.id}` : t("Usuario no encontrado");
    }
    const fromArray =
      _.get(jaguarState, enums.typeUserRoutes[interview.interviewed?.from])
        ?.formatted ?? [];
    const user = fromArray.find(
      (user) => user.value === interview.interviewed?.id
    )?.label;
    return user ? `de ${user}` : t("Usuario no encontrado");
  }, [interview.interviewed]);

  const scheduledDate = useMemo(() => {
    const fecha = dayjs(interview.scheduled).format("DD/MMM/YYYY");
    if (fecha == "Invalid Date") return t("No programada");
    const hora = interview.timeScheduled ? `- ${interview.timeScheduled}` : "";
    return `${fecha.toLocaleUpperCase()} ${hora.toLocaleLowerCase()}`;
  }, [interview.scheduled, interview.timeScheduled]);

  const participants = useMemo(
    () => interview.witness ?? [],
    [interview.witness]
  );

  const attachments = useMemo(() => {
    if (Array.isArray(interview.attachments)) return interview.attachments;
    if (
      Array.isArray(interview.attachments.principal) &&
      Array.isArray(interview.attachments.signed) &&
      Array.isArray(interview.attachments.support)
    ) {
      return [
        ...interview.attachments.principal,
        ...interview.attachments.signed,
        ...interview.attachments.support,
      ];
    }
    return [];
  }, [interview.attachments]);

  const evaluationPoints = useMemo(() => {
    if (interview.evaluation == null) return [];
    let points = Object.entries(interview.evaluation.points).map(
      ([key, value]) => ({ key, value })
    );
    points = points.filter((point) => point.value.value);
    return points;
  }, [interview.evaluation?.points]);

  const handleEditInterview = () => {
    setSelectedInterview(idx);
    setIsAdding(true);
  };

  const openModalDeleteInterview = () => {
    openConfirmDeleteModal({ onConfirm: () => handleDeleteInterview() });
  };

  const handleDeleteInterview = async () => {
    const newInterviews = [...interviews];
    newInterviews.splice(idx, 1);
    try {
      await handleSaveInterviews(newInterviews);
      closeModalConfirm();
    } catch (error) {
      console.log(error);
    }
  };

  const actions = [
    {
      name: t("causes.edit"),
      icon: <Icon name="outline_edit" color={theme.palette.primary.main} />,
      onClick: handleEditInterview,
    },
    {
      name: t("Imprimir"),
      icon: <Icon name="printer" color={theme.palette.blueGray700.main} />,
      onClick: () => {},
    },
    {
      name: t("causes.delete"),
      icon: <Icon name="trash_can" color={theme.palette.error.main} />,
      onClick: openModalDeleteInterview,
    },
  ];

  return (
    <>
      {!justView && (
        <>
          <AddQuestionsToInterview // Modal
            show={showAddQuestionsToInterview}
            onHide={() => setShowAddQuestionsToInterview(false)}
            selectedQuestionForEdit={selectedQuestionForEdit}
            setSelectedQuestionForEdit={setSelectedQuestionForEdit}
            idx={idx}
            interviews={interviews}
            handleSaveInterviews={handleSaveInterviews}
            alias={alias}
            interviewed={interview.interviewed?.id}
            from={interview.interviewed?.from}
            onReload={onReload}
          />
          <AddAttachments // Modal
            show={showAddAttachments}
            onHide={() => setShowAddAttachments(false)}
            idx={idx}
            interviews={interviews}
            setInterviews={setInterviews}
            handleSaveInterviews={handleSaveInterviews}
            typeFileOpts={typeFileOpts}
            currentOptions={setSelectTypeFileOpts}
          />
          <EvaluateInterview // Drawer
            open={showEvaluateInterview}
            onClose={() => setShowEvaluateInterview(false)}
            idx={idx}
            interviews={interviews}
            setInterviews={setInterviews}
            handleSaveInterviews={handleSaveInterviews}
          />
          <AddConclusion
            show={showAddConclusion} // Modal
            onHide={() => setShowAddConclusion(false)}
            idx={idx}
            interviews={interviews}
            setInterviews={setInterviews}
            handleSaveInterviews={handleSaveInterviews}
            alias={alias}
            interviewer={interview.interviewed}
          />
        </>
      )}
      <EGAccordionSingle
        title={`${t("Entrevista")} ${interviewed}`}
        bgColor={theme.palette.blueGray100.main}
        color={theme.palette.oxfordBlue.main}
        actions={justView ? undefined : actions}
      >
        <Grid2 container spacing={1.8} className="interview-container">
          <Grid2 size={{ xs: 12, lg: 4 }}>
            <InvestigationItemContainer
              sx={{ padding: "10px 15px", display: "inline-block" }}
              width="fit-content"
              color={theme.palette.blueGray100.main}
            >
              <Typography
                fontSize={14}
                fontWeight={600}
                color="blueGray900"
                display="inline"
              >
                {t("Entrevista")}{" "}
                {statusOpts
                  .find((opt) => opt.value === interview.status)
                  ?.label?.toLocaleLowerCase() ?? t("Sin definir")}
              </Typography>
            </InvestigationItemContainer>
          </Grid2>

          <Grid2 size={{ xs: 12, lg: 4 }}>
            <Typography
              display="inline"
              color="blueGray900"
              fontWeight={600}
              fontSize={14}
            >
              {t("Fecha de la entrevista")}: &nbsp;
            </Typography>
            <Typography
              display="inline"
              color="primary"
              fontWeight={700}
              fontSize={16}
            >
              {scheduledDate}
            </Typography>
          </Grid2>

          <Grid2 size={{ xs: 12, lg: 4 }}>
            <InvestigationBlockInfo
              label={t("Entrevistador")}
              text={getUserLabel(interview.interviewer)}
            />
          </Grid2>

          {participants.length > 0 && (
            <Grid2 size={12}>
              <Typography
                fontSize={14}
                fontWeight={600}
                color={theme.palette.secondary.main}
              >
                Participantes
              </Typography>
              <Stack
                spacing={1}
                direction={"row"}
                flexWrap={"wrap"}
                gap={1}
                mt={1}
              >
                {participants.map((participant, idx) => (
                  <ChipParticipant
                    key={idx}
                    role={
                      rolesOpts.find((opt) => opt.value === participant.role)
                        ?.label
                    }
                    name={getUserLabel(participant)}
                  />
                ))}
              </Stack>
            </Grid2>
          )}

          {interview.details && (
            <Grid2 size={{ xs: 12 }}>
              <InvestigationBlockInfo
                label={t("Objetivos de la entrevista")}
                text={interview.details}
              />
            </Grid2>
          )}

          <Grid2 size={{ xs: 12 }}>
            <InvestigationItemContainer
              color={theme.palette.blueGray200.main}
              sx={{ padding: 2 }}
            >
              <Typography fontSize={14} fontWeight={600} display="inline">
                Entrevista&nbsp;
                {interview.kind === "in-person"
                  ? typeInterviewOpts
                      .find((opt) => opt.value === interview.kind)
                      ?.label.toLocaleLowerCase()
                  : `en ${
                      typeInterviewOpts.find(
                        (opt) => opt.value === interview.kind
                      )?.label
                    }`.toLocaleLowerCase()}
              </Typography>

              {!!interview.kindDetails && (
                <>
                  <hr />
                  <Typography fontSize={16}>{interview.kindDetails}</Typography>
                </>
              )}
            </InvestigationItemContainer>
          </Grid2>
        </Grid2>

        {interview.interview?.length > 0 && (
          <>
            <Separator
              text={t("Preguntas y respuestas")}
              iconButton="printer"
              onClick={() => {}}
              sx={{ my: 2 }}
            />

            <Stack spacing={1.2}>
              {interview.interview.map((question, idx) => (
                <InvestigationCollapsable
                  key={idx}
                  title={
                    <Stack spacing={0.2}>
                      <Typography
                        fontSize={16}
                        fontWeight={600}
                        color="secondary.main"
                      >
                        {question.question}
                      </Typography>
                      {question.details && (
                        <Typography
                          component="span"
                          fontSize={12}
                          fontWeight={600}
                          color={theme.palette.blueGray600.main}
                        >
                          {t("Detalles de la pregunta")}: &nbsp;
                          <Typography
                            component="span"
                            fontSize={12}
                            fontWeight={400}
                            color={theme.palette.oxfordBlue.main}
                            fontStyle="italic"
                          >
                            {question.details}
                          </Typography>
                        </Typography>
                      )}
                    </Stack>
                  }
                  details={
                    question.answer && (
                      <Stack>
                        <Typography
                          fontSize={14}
                          fontWeight={600}
                          color={theme.palette.blueGray600.main}
                        >
                          {t("Respuesta")}:
                        </Typography>
                        <RichTextEditor
                          value={question.answer}
                          justView={true}
                        />
                        {question.answerDetails && (
                          <Typography
                            component="span"
                            fontSize={12}
                            fontWeight={600}
                            color={theme.palette.blueGray600.main}
                          >
                            {t("Detalles de la respuesta")}: &nbsp;
                            <Typography
                              component="span"
                              fontSize={12}
                              fontWeight={400}
                              color={theme.palette.oxfordBlue.main}
                              fontStyle="italic"
                            >
                              {question.answerDetails}
                            </Typography>
                          </Typography>
                        )}
                      </Stack>
                    )
                  }
                />
              ))}
            </Stack>
          </>
        )}

        {attachments.length > 0 && (
          <>
            <Separator
              text={t("Archivos de soporte de entrevista")}
              sx={{ my: 2 }}
            />

            <Grid2 container spacing={2}>
              {attachments.map((item, idx) => (
                <Grid2 size={{ xs: 12, md: 4 }} key={idx}>
                  <UploadedFile
                    text={
                      typeFileOpts.find((opt) => opt.value === item.purpose)
                        ?.label || item.name
                    }
                    url={`url`}
                  />
                </Grid2>
              ))}
            </Grid2>
          </>
        )}

        {interview.evaluation != null && (
          <>
            <Separator
              text={t("Evaluación de la entrevista")}
              iconButton="printer"
              onClick={() => {}}
              sx={{ my: 2 }}
            />

            <Grid2 container spacing={2}>
              {evaluationPoints.map(({ key, value }) => (
                <Grid2 size={{ xs: 12, md: 6 }} key={key}>
                  <EvaluationPoint
                    point={key}
                    value={value.value}
                    label={pointsToEvaluateLabels[key] || key}
                    details={value.details}
                    options={options}
                  />
                </Grid2>
              ))}
            </Grid2>
            {!!interview.evaluation?.details && (
              <InvestigationBlockInfo
                label={t("Detalles adicionales de evaluación")}
                text={`${interview.evaluation.details}`}
                sx={{ mt: 2 }}
              />
            )}
          </>
        )}

        {interview.conclusion !== null && (
          <>
            <Separator text={t("Conclución de la entrevista")} sx={{ my: 2 }} />

            <Grid2 container spacing={1.5}>
              {interview.conclusion?.conclusion && (
                <InvestigationBlockInfo
                  label={t("Conclusión")}
                  text={
                    <Box pb={0.5}>
                      <RichTextEditor
                        value={interview.conclusion?.conclusion}
                        justView={true}
                      />
                    </Box>
                  }
                  size={12}
                />
              )}
              {interview.conclusion?.attachments?.map((item, idx) => (
                <Grid2 size={{ xs: 12, md: 4 }} key={idx}>
                  <TemporalUploadedFile
                    name={item.name}
                    size={item.size}
                    iconName="outline_import"
                    iconAction={() => {}}
                    iconColor={theme.palette.secondary.main}
                    width="100%"
                  />
                </Grid2>
              ))}
            </Grid2>
          </>
        )}

        {!justView && (
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="flex-end"
            spacing={1}
          >
            {!interview.conclusion && (
              <Button
                onClick={() => setShowAddQuestionsToInterview(true)}
                sx={{
                  background:
                    "linear-gradient(180deg, #7bbdff 0%, #2563eb 100%)",
                  border: `1px solid ${theme.palette.primary.main}`,
                  padding: "6px 16px",
                  position: "relative",
                  zIndex: 1,
                  mt: 2,
                  "& .outline_ai": {
                    display: "block",
                  },
                  "& .bold_ai": {
                    display: "none",
                  },
                  "&:hover": {
                    background:
                      "linear-gradient(180deg, #55e5b8 0%, #10b981 100%)",
                    border: `1px solid ${theme.palette.teal.main}`,
                    "& .outline_ai": {
                      display: "none",
                    },
                    "& .bold_ai": {
                      display: "block",
                    },
                  },
                }}
              >
                <Icon
                  name="outline_ai"
                  color="white"
                  size={20}
                  className="outline_ai"
                />
                <Icon
                  name="bold_ai"
                  color="white"
                  size={20}
                  className="bold_ai"
                />
                <Typography fontSize={13} fontWeight={600} color="white" ml={1}>
                  {`${t("AGREGAR PREGUNTAS")}`}
                </Typography>
              </Button>
            )}
            {/* {
              !interview.conclusion &&
              <InvestigationButton
                label={`${t("AGREGAR PREGUNTAS")}`}
                variant="main"
                outlined
                onClick={() => setShowAddQuestionsToInterview(true)}
                sx={{ mt: 2 }}
              />
            } */}
            {selectTypeFileOpts.length > 0 && (
              <InvestigationButton
                label={`${t("SUBIR ARCHIVOS")}`}
                onClick={() => setShowAddAttachments(true)}
                sx={{ mt: 2 }}
              />
            )}
            {interview.evaluation === null && interview.status === "finish" && (
              <InvestigationButton
                label={`${t("EVALUAR ENTREVISTA")}`}
                variant="main"
                onClick={() => setShowEvaluateInterview(true)}
                width={180}
                sx={{ mt: 2 }}
              />
            )}
            {interview.conclusion === null && interview.evaluation && (
              <InvestigationButton
                label={`${t("AGREGAR CONCLUSIÓN")}`}
                variant="custom"
                bg={theme.palette.secondary.main}
                inverted
                onClick={() => setShowAddConclusion(true)}
                width={180}
                sx={{ mt: 2 }}
              />
            )}
          </Stack>
        )}
      </EGAccordionSingle>
    </>
  );
};
